import { useEffect, useState } from "react";
import type { WeekKey } from "@/components/effective-plan/planFormat";
import {
  saveBlob,
  timetableExportApi,
  type ExportOptions,
  type ExportRequest,
} from "@/services/api/timetableExport";

const PERIODS = [
  ["selected_week", "Selektierte Woche"],
  ["this_week", "Diese Woche"],
  ["one_week", "Eine Woche"],
  ["range", "Bereich"],
  ["semester", "Semester"],
  ["from_today", "Ab heute"],
] as const;

const MARKS = [
  ["substitution", "Vertretung"],
  ["gap", "Lücke"],
  ["cancelled", "Ausfall"],
] as const;

const PARTS = [
  ["teachers", "Lehrer"],
  ["classes", "Klassen"],
  ["changes", "Liste"],
  ["combined", "Sammeldatei"],
] as const;

const FORMAT_LABEL: Record<string, string> = {
  pdf: "PDF",
  untis_substitution: "Untis",
  webuntis_excel: "WebUntis",
  stida_excel: "STiDA",
  lehreroffice_gpuntis: "LehrerOffice",
  schulnetz: "Schulnetz",
};

function mondayOfIsoWeek(year: number, week: number): string {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (week - 1) * 7);
  const y = monday.getUTCFullYear();
  const m = String(monday.getUTCMonth() + 1).padStart(2, "0");
  const d = String(monday.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const MARK_ON: Record<string, string> = {
  substitution: "border-green-600 bg-green-100 text-green-800",
  gap: "border-orange-600 bg-orange-100 text-orange-800",
  cancelled: "border-red-600 bg-red-100 text-red-800",
};

function chipClass(on: boolean, disabled = false): string {
  if (disabled)
    return "rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-400";
  return on
    ? "rounded border border-blue-600 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700"
    : "rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 hover:border-blue-300 hover:bg-blue-50/60";
}

function markChipClass(id: string, on: boolean): string {
  const base = "rounded border px-2 py-1 text-xs";
  if (!on)
    return `${base} border-gray-200 bg-white text-gray-700 hover:border-gray-300`;
  return `${base} font-medium ${MARK_ON[id] || ""}`;
}

async function readDetail(err: unknown): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { detail?: string };
      if (typeof parsed.detail === "string") return parsed.detail;
    } catch {
      return "Drucken fehlgeschlagen.";
    }
  }
  const detail = (data as { detail?: string } | undefined)?.detail;
  return typeof detail === "string" ? detail : "Drucken fehlgeschlagen.";
}

export function TeacherPrintBar({
  options,
  week,
  subjectTeacherId,
  onClose,
  note = "Eigene Stunden plus übernommene. Meier bleibt Lehrer.",
}: {
  options: ExportOptions;
  week: WeekKey | null;
  subjectTeacherId?: string | null;
  onClose: () => void;
  note?: string;
}) {
  const [period, setPeriod] = useState<
    | "selected_week"
    | "this_week"
    | "one_week"
    | "range"
    | "semester"
    | "from_today"
  >(week ? "selected_week" : "this_week");
  const [touched, setTouched] = useState(false);
  const [weekStart, setWeekStart] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [horizon, setHorizon] = useState(
    options.horizon_days || options.preference?.period_n || 14
  );
  const [marks, setMarks] = useState<string[]>(
    options.preference?.change_types || ["substitution", "gap", "cancelled"]
  );
  const [nameMode, setNameMode] = useState(
    options.preference?.name_mode || "readable"
  );
  const [parts, setParts] = useState<string[]>(
    options.preference?.pdf_parts || [
      "teachers",
      "classes",
      "changes",
      "combined",
    ]
  );
  const [formats, setFormats] = useState<string[]>(["pdf"]);
  const [to, setTo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!touched) setPeriod(week ? "selected_week" : "this_week");
  }, [week, touched]);

  const choosePeriod = (next: typeof period) => {
    setTouched(true);
    setPeriod(next);
  };

  const toggle = (
    list: string[],
    value: string,
    setList: (next: string[]) => void
  ) => {
    setList(
      list.includes(value)
        ? list.filter((item) => item !== value)
        : [...list, value]
    );
  };

  const payload = (): ExportRequest | null => {
    let kind: ExportRequest["period_kind"] =
      period === "selected_week" ? "one_week" : period;
    let start = weekStart;
    if (period === "selected_week") {
      if (!week) return null;
      kind = "one_week";
      start = mondayOfIsoWeek(week.iso_year, week.iso_week);
    }
    return {
      period_kind: kind,
      period_n: horizon,
      change_types: marks,
      name_mode: nameMode,
      pdf_parts: formats.includes("pdf") ? parts : [],
      week_start: start || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      formats: formats.filter((id) =>
        options.formats.some((item) => item.id === id && item.capable)
      ),
      to: to || undefined,
      subject_teacher_id: subjectTeacherId || undefined,
    };
  };

  const run = async (kind: "download" | "mail") => {
    const body = payload();
    if (!body) {
      setError("Oben eine Woche wählen, oder Diese Woche nehmen.");
      return;
    }
    if (period === "one_week" && !weekStart) {
      setError("Für Eine Woche ein Datum wählen.");
      return;
    }
    if (period === "range" && (!dateFrom || !dateTo)) {
      setError("Für den Bereich Von und Bis wählen.");
      return;
    }
    if (period === "range" && dateFrom > dateTo) {
      setError("Von liegt nach Bis.");
      return;
    }
    if (!body.pdf_parts.length) {
      setError(
        "Mindestens ein PDF-Teil, solange kein Programmformat ein Feld hat."
      );
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (kind === "download") {
        const response = await timetableExportApi.download(body);
        saveBlob(response.data, "stundenplan.zip");
      } else {
        await timetableExportApi.mail(body);
        setInfo("Der Link ist in der Mail.");
      }
    } catch (err: unknown) {
      setError(await readDetail(err));
    } finally {
      setBusy(false);
    }
  };

  const selectedLabel = week ? `KW ${week.iso_week}` : "keine Woche oben";

  return (
    <aside
      aria-label="Drucken"
      className="absolute bottom-2 right-2 top-2 z-30 flex w-72 flex-col overflow-hidden rounded-lg border border-gray-900 bg-white shadow-lg"
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
        <span className="text-xs font-semibold text-gray-900">Drucken</span>
        <button
          type="button"
          className="text-xs text-gray-500"
          onClick={onClose}
        >
          Schliessen
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto px-3 py-3">
        <div>
          <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
            Zeit
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {PERIODS.map(([id, label]) => (
              <button
                key={id}
                type="button"
                disabled={id === "selected_week" && !week}
                title={
                  id === "selected_week"
                    ? "Die Woche aus der Leiste oben"
                    : id === "this_week"
                      ? "Die laufende Kalenderwoche"
                      : undefined
                }
                className={chipClass(
                  period === id,
                  id === "selected_week" && !week
                )}
                onClick={() => choosePeriod(id)}
              >
                {id === "selected_week" ? "Selektierte Woche" : label}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-gray-500">
            {period === "selected_week"
              ? `Oben gewählt: ${selectedLabel}`
              : period === "this_week"
                ? "Jetzt, Montag bis Sonntag"
                : null}
          </p>
          {period === "one_week" ? (
            <input
              className="mt-2 w-full rounded border border-gray-200 px-2 py-1 text-xs"
              type="date"
              value={weekStart}
              onChange={(event) => setWeekStart(event.target.value)}
            />
          ) : null}
          {period === "range" ? (
            <div className="mt-2 flex gap-1.5">
              <input
                className="w-full rounded border border-gray-200 px-2 py-1 text-xs"
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
              />
              <input
                className="w-full rounded border border-gray-200 px-2 py-1 text-xs"
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
              />
            </div>
          ) : null}
          {period === "from_today" ? (
            <input
              className="mt-2 w-16 rounded border border-gray-200 px-2 py-1 text-xs"
              type="number"
              min={1}
              value={horizon}
              onChange={(event) => setHorizon(Number(event.target.value))}
            />
          ) : null}
        </div>
        <div>
          <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
            Marke
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {MARKS.map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={markChipClass(id, marks.includes(id))}
                onClick={() => toggle(marks, id, setMarks)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
            Namen
          </h3>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              className={chipClass(nameMode === "readable")}
              onClick={() => setNameMode("readable")}
            >
              Lesbar
            </button>
            <button
              type="button"
              className={chipClass(nameMode === "code")}
              onClick={() => setNameMode("code")}
            >
              Kürzel
            </button>
          </div>
        </div>
        <div>
          <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
            Teile
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {PARTS.map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={chipClass(parts.includes(id))}
                onClick={() => toggle(parts, id, setParts)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
            Format
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {options.formats.map((item) => (
              <button
                key={item.id}
                type="button"
                disabled={!item.capable}
                title={item.capable ? undefined : item.reason}
                className={chipClass(
                  item.capable && formats.includes(item.id),
                  !item.capable
                )}
                onClick={() => {
                  if (item.capable) toggle(formats, item.id, setFormats);
                }}
              >
                {FORMAT_LABEL[item.id] || item.id}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-gray-500">
            Ohne belegtes Feld wird keine Programmdatei erzeugt.
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-1.5">
          {options.can_mail && options.teacher_mail_mode === "own" ? (
            <span className="inline-flex justify-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
              eigene Adresse
            </span>
          ) : null}
          {options.teacher_mail_mode === "free" ? (
            <input
              className="rounded border border-gray-200 px-2 py-1 text-xs"
              placeholder="Adresse"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          ) : null}
          <button
            type="button"
            className="rounded bg-blue-600 px-2 py-1.5 text-xs font-medium text-white disabled:opacity-40"
            disabled={busy}
            onClick={() => run("download")}
          >
            PDF laden
          </button>
          {options.can_mail &&
          options.teacher_mail_mode !== "download" &&
          options.teacher_mail_mode !== "school" ? (
            <button
              type="button"
              className="rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 disabled:opacity-40"
              disabled={busy}
              onClick={() => run("mail")}
            >
              Link an mich
            </button>
          ) : null}
        </div>
        <div className="flex flex-col gap-1 border-t border-gray-100 pt-2 text-xs text-gray-500">
          <span>{note}</span>
          <span>Montag-Mail unverändert.</span>
        </div>
        {error ? <p className="text-xs text-red-700">{error}</p> : null}
        {info ? <p className="text-xs text-green-700">{info}</p> : null}
      </div>
    </aside>
  );
}
